import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import {
  applySpec,
  assoc,
  compose,
  curry,
  equals,
  flip,
  head,
  ifElse,
  includes,
  isEmpty,
  keys,
  last,
  map,
  reduce,
  reject,
  replace,
  path,
  split,
  toPairs,
  trim,
  __,
} from 'ramda';
import { withStyles } from '@material-ui/core/styles';
import Table from '@material-ui/core/Table';
import Checkbox from '@material-ui/core/Checkbox';
import TableHead from '@material-ui/core/TableHead';
import TableBody from '@material-ui/core/TableBody';
import TableRow from '@material-ui/core/TableRow';
import TableCell from '@material-ui/core/TableCell';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Switch from '@material-ui/core/Switch';
import { stylesToPropType } from '../../../utils';

const BASE_SCOPE = 'client';
const CLIENT_BASE_SCOPE = 'irisbaseclient';
const SERVER_BASE_SCOPE = 'irisbaseserver';

const KNOWN_SCOPE_ABBREVIATIONS = Object.freeze({
  idm: 'Identity Manager',
  ntm: 'Notification Manager',
  em: 'Event Manager',
  doc: 'Document Store',
  eks: 'Encrypted Keystore',
  cour: 'Courier Messaging Service',
  ids: 'Iris Data Store',
});

const BASE_SCOPE_MODEL = Object.freeze(reduce(
  flip(assoc(__, { read: false, write: false }, __)),
  {},
  keys(KNOWN_SCOPE_ABBREVIATIONS),
));

const applyOldSpec = applySpec({
  abbreviation: compose(head, split(':')),
  read: compose(includes('read'), last, split(':')),
  write: compose(includes('write'), last, split(':')),
});

const applyNewSpec = applySpec({
  abbreviation: compose(last, split(':')),
  read: compose(includes('read'), head, split(':')),
  write: compose(includes('write'), head, split(':')),
});


const inflateScope = (n) => {
  const vals = split(':', n);
  const val = last(vals);
  let returnVals = null;
  if (includes('read', val) || includes('write', val)) returnVals = applyOldSpec(n);
  else returnVals = applyNewSpec(n);
  return returnVals;
};

const buildScopeModel = reduce(
  (accum, { abbreviation, read, write }) => assoc(abbreviation, { read, write }, accum),
  BASE_SCOPE_MODEL,
);

const updateScopeString = curry((abbreviation, read, write, scopeString) => {
  // Added to avoid adding empty scope to the scope string.
  const abbr = (read || write) ? `:${abbreviation}` : '';

  const newScope = `${read ? 'read' : ''}${write ? 'write' : ''}${abbr}`;
  let returnScope = '';
  if (includes(`${abbreviation}:`, scopeString)) {
    returnScope = trim(replace(new RegExp(`${abbreviation}:(readwrite|read|write)?`), newScope, scopeString));
  } else if (includes(`:${abbreviation}`, scopeString)) {
    returnScope = trim(replace(new RegExp(`(readwrite|read|write)?:${abbreviation}`), newScope, scopeString));
  } else {
    returnScope = `${scopeString} ${newScope}`;
  }
  return returnScope;
});

const consolidateSpaces = replace(/( )+/g, ' ');

const styles = () => ({
  disabled: {
    '& *': {
      color: '#ccc',
      cursor: 'default',
    },
    '& input': {
      pointerEvents: 'none',
    },
  },
});

const isBaseScopes = (n) => {
  return equals(n, BASE_SCOPE) || equals(n, CLIENT_BASE_SCOPE) || equals(n, SERVER_BASE_SCOPE);
};

// const expandIdsScopes = (scopeString) => {
//   return includes('ids', scopeString) && !includes('ids:', scopeString)
//     ? trim(replace('ids', 'ids:readwrite', scopeString))
//     : scopeString;
// };

const UnstyledScopeEditor = ({
  classes,
  baseScope,
  scopeString,
  onChange,
  disabled,
}) => {
  const scopes = reject(isEmpty, split(' ', scopeString));
  const baseScopeEnabled = includes(BASE_SCOPE, scopes) || includes(baseScope, scopes);
  const inflatedScopes = map(inflateScope, reject(isBaseScopes, scopes));
  const scopeModel = buildScopeModel(inflatedScopes);
  const scopeModelPairs = toPairs(scopeModel);

  return (
    <Fragment>
      <FormControlLabel
        control={(
          <Switch
            checked={baseScopeEnabled}
            onChange={compose(
              onChange,
              consolidateSpaces,
              ifElse(
                path(['target', 'checked']),
                () => `${baseScope} ${scopeString}`,
                () => replace(new RegExp(`(${BASE_SCOPE}|${baseScope})`), '', scopeString),
              ),
            )}
            value="enable-base-scope"
            color="primary"
            disabled={disabled}
          />
        )}
        label="enable scope"
      />

      <Table className={baseScopeEnabled ? '' : classes.disabled}>
        <TableHead>
          <TableRow>
            <TableCell colSpan="4">Scope</TableCell>
            <TableCell>Read</TableCell>
            <TableCell>Write</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {map(([abbreviation, { read, write }]) => (
            <TableRow key={abbreviation}>
              <TableCell colSpan="4">{KNOWN_SCOPE_ABBREVIATIONS[abbreviation] || abbreviation}</TableCell>
              <TableCell>
                <Checkbox
                  checked={read}
                  color="default"
                  value={`${abbreviation}-read`}
                  onChange={compose(
                    onChange,
                    consolidateSpaces,
                    updateScopeString(abbreviation, __, write, scopeString),
                    path(['target', 'checked']),
                  )}
                  disabled={disabled}
                />
              </TableCell>
              <TableCell>
                <Checkbox
                  checked={write}
                  color="default"
                  value={`${abbreviation}-write`}
                  onChange={compose(
                    onChange,
                    consolidateSpaces,
                    updateScopeString(abbreviation, read, __, scopeString),
                    path(['target', 'checked']),
                  )}
                  disabled={disabled}
                />
              </TableCell>
            </TableRow>
          ), scopeModelPairs)}
        </TableBody>
      </Table>
    </Fragment>
  );
};

UnstyledScopeEditor.propTypes = {
  classes: stylesToPropType(styles()).isRequired,
  baseScope: PropTypes.string.isRequired,
  scopeString: PropTypes.string.isRequired,
  onChange: PropTypes.func,
  disabled: PropTypes.bool,
};

UnstyledScopeEditor.defaultProps = {
  onChange: () => {},
  disabled: false,
};

export default withStyles(styles)(UnstyledScopeEditor);
